import styled from "@emotion/styled/macro";
import {
  tokens,
  Typography,
  Snackbar,
  Button,
  LoadingIndicator,
  LoadingOverlay,
} from "@sunrun/experience-ui-components";
import React, { useState, useMemo } from "react";
import { getEnvironment } from "@sunrun/sales-experience-shared";
import { CheckoutFooter } from "../molecules/CheckoutFooter";
import { DownloadCustomerApp } from "../atoms/DownloadCustomerApp";
import { useAppSelector } from "../../../store";
import { SplatProspect } from "../../../../amplify/backend/function/OfferExpContacts/ts/public/types";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { useCheckoutFooter } from "checkout/providers/CheckoutFooterContext";
import { Box } from "components/atoms/Box";
import { useProspect } from "checkout/hooks/useCheckoutSwr";
import { sendCustomerAppDownloadEmail } from "services/email";
import { sendCustomerAppDownloadText } from "services/messages";
import { Mixpanel } from "providers/mixpanel";
import {
  CUSTOMER_SELF_CHECKOUT__CLICKED_SEND_INTRO_MESSAGE_TO_CUSTOMER_IN_CUSTOMER_APP_TASK_VIEW,
  CUSTOMER_SELF_CHECKOUT__CLICKED_SEND_FOLLOW_UP_MESSAGE_IN_CUSTOMER_APP_TASK_VIEW,
} from "constants/mixpanelEvents";
import { rollbar } from "providers/rollbar";
import { useCustomerAppSessionFlag } from "checkout/hooks/useCustomerAppSessionFlag";

const CustomerAppStandardTaskView: React.FC = () => {
  const { prospectId, navigateToNextTask, offer, userProfile } =
    useCheckoutContext();
  const authKey = useAppSelector((state) => state?.auth?.hybridToken);
  const isPartner = userProfile?.isPartner;

  const [loadingOverlay, setLoadingOverlay] = useState({
    message: "",
    show: false,
  });
  const [snackbar, setSnackbar] = useState({
    message: "",
    show: false,
    type: "success",
  });

  const [isIntroMessageSent, setIsIntroMessageSent] = useCustomerAppSessionFlag(
    prospectId,
    offer?.id ?? ""
  );

  const { data: prospect, isLoading: isProspectLoading } =
    useProspect(prospectId);

  const prospectData = prospect as SplatProspect;

  const handleSendFollowUpMessage = () => {
    Mixpanel.track(
      CUSTOMER_SELF_CHECKOUT__CLICKED_SEND_FOLLOW_UP_MESSAGE_IN_CUSTOMER_APP_TASK_VIEW,
      {
        prospectId,
        salesRep: prospectData?.salesRep,
        salesRepEmail: prospectData?.salesRepEmail,
        salesRepName: prospectData?.salesRepName,
        opportunityStage: prospectData?.leadStage,
      }
    );
  };

  const handleSendMessage = async () => {
    Mixpanel.track(
      CUSTOMER_SELF_CHECKOUT__CLICKED_SEND_INTRO_MESSAGE_TO_CUSTOMER_IN_CUSTOMER_APP_TASK_VIEW,
      {
        prospectId,
        salesRep: prospectData?.salesRep,
        salesRepEmail: prospectData?.salesRepEmail,
        salesRepName: prospectData?.salesRepName,
        opportunityStage: prospectData?.leadStage,
      }
    );

    setLoadingOverlay({ message: "Sending message...", show: true });

    try {
      await sendCustomerAppDownloadEmail(authKey, prospectId);
      await sendCustomerAppDownloadText(authKey, prospectId);

      setSnackbar({
        message: "Message sent successfully!",
        show: true,
        type: "success",
      });

      setIsIntroMessageSent(true);
    } catch (error) {
      rollbar.error(error as Error);
      setSnackbar({
        message: "Sorry, we couldn't send your message.",
        show: true,
        type: "error",
      });
    } finally {
      setLoadingOverlay({ message: "", show: false });
    }
  };

  const messagesUrl = useMemo(() => {
    return getEnvironment() === "production"
      ? "https://sunrunone.com/messages"
      : "https://staging.sunrunone.com/messages";
  }, []);

  useCheckoutFooter(
    isProspectLoading ? null : (
      <CheckoutFooter
        buttonText="Confirm"
        onClick={navigateToNextTask}
        isDisabled={!isIntroMessageSent}
      />
    ),
    [isProspectLoading, navigateToNextTask, isIntroMessageSent]
  );

  if (isProspectLoading) {
    return (
      <LoadingWrapper>
        <LoadingIndicator color="black" />
      </LoadingWrapper>
    );
  }

  return (
    <Box display="flex" flexDirection="column">
      <InformationContainer data-testid="validation-info">
        <Typography size={tokens.FONT_SIZE_1} color={tokens.BRAND_HEROBLUE}>
          Please make sure the customer has downloaded the mobile app. Scan the
          QR code, send it by email or text message with the the app download
          link
        </Typography>
        <Box
          display="flex"
          flexDirection="row"
          gap="32px"
          alignItems="center"
          marginTop="4px"
        >
          <Button
            color="secondary"
            size="sm"
            onClick={handleSendMessage}
            disabled={loadingOverlay.show || !prospectId || isIntroMessageSent}
          >
            Send intro message to customer
          </Button>
          {isIntroMessageSent && (
            <StyledAnchor
              href={messagesUrl}
              target="_blank"
              rel="noreferrer"
              onClick={handleSendFollowUpMessage}
            >
              Send a follow-up message (Optional)
            </StyledAnchor>
          )}
        </Box>
      </InformationContainer>
      <Container>
        <DownloadCustomerApp isPartner={Boolean(isPartner)} />
      </Container>
      <Snackbar
        data-testid="bottom-snackbar"
        open={snackbar.show}
        onClose={() =>
          setSnackbar({ message: "", show: false, type: "success" })
        }
        message={snackbar.message}
        type={snackbar.type as "success" | "error"}
      />
      {loadingOverlay.show && (
        <LoadingOverlay
          message={loadingOverlay.message}
          data-testid="customer-app-task-loading-overlay"
        />
      )}
    </Box>
  );
};

const LoadingWrapper = styled.div`
  margin-top: 32px;
`;

const InformationContainer = styled.div`
  margin-bottom: 16px;
  > p {
    padding-bottom: 16px;
  }
`;

const Container = styled.div`
  margin-top: 26px;
`;

const StyledAnchor = styled.a`
  text-decoration: underline;
  text-underline-offset: 6px;
  text-decoration-thickness: 1px;
  font-weight: 500;
  height: 30px;
  color: ${tokens.BRAND_HEROBLUE};
  font-size: ${tokens.FONT_SIZE_1}px;
`;

export { CustomerAppStandardTaskView };
